import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, ShieldCheck, Eye, Languages, Compass } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import SafetyLink from "@/components/SafetyLink";
import { useApp } from "@/lib/store";

const STEPS = [
  { icon: Eye, title: "Recognize", body: "Name what feels off in a single tap. No forms, no journaling." },
  { icon: Languages, title: "Translate", body: "Signal State ID reads it into one of six clear response modes." },
  { icon: Compass, title: "Respond", body: "Get one realistic action, sized to the time you actually have." },
];

const QUOTES = [
  { q: "It gives me one thing to do instead of a spiral of everything.", who: "Team lead" },
  { q: "The first tool that didn't try to diagnose me or make me keep a streak.", who: "Parent of two" },
  { q: "Sixty seconds, and I actually know what I need next.", who: "Founder" },
];

export default function Intro() {
  const nav = useNavigate();
  const { canInstall, promptInstall, user, authReady } = useApp();

  useEffect(() => {
    if (!authReady) return;
    if (user || localStorage.getItem("ss_seen_intro") === "true") nav("/home", { replace: true });
  }, [authReady, user, nav]);

  const start = (baseline = false) => {
    localStorage.setItem("ss_seen_intro", "true");
    nav(baseline ? "/navigate?baseline=1" : "/home");
  };

  if (!authReady || user) return null;

  return (
    <Shell>
      <div className="px-6 pt-12 pb-16">
        {/* Hero (static — always visible) */}
        <div>
          <Brandmark size={34} className="mb-1" />

          <div className="mt-8">
            <h1 className="font-serif-display text-5xl sm:text-6xl leading-[1.05] text-[#1A1A1A]">
              <span className="block">Something</span>
              <span className="block">feels off.</span>
            </h1>
            <p className="mt-5 text-2xl font-serif-display text-[#474747]">
              You don't need the words for it yet.
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-[#474747] max-w-sm">
              Signal State ID is a private way to recognize how you're really operating and get{" "}
              <span className="text-[#1A1A1A] font-medium">one realistic next move</span> — in about a
              minute. No diagnosis. No journaling. No streaks to keep.
            </p>
          </div>

          <div className="mt-9 space-y-3">
            <button data-testid="intro-start"
              onClick={() => start(false)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5">
              Enter Signal State ID <ArrowRight size={18} strokeWidth={1.8} />
            </button>
            <button data-testid="intro-baseline"
              onClick={() => start(true)}
              className="w-full text-center text-[15px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors py-2">
              I feel steady and want to record a baseline
            </button>
          </div>

          <p className="mt-6 text-[13px] text-[#6B6B6B]">
            Private · nonclinical · no streaks
          </p>
        </div>

        {/* How it works */}
        <section className="mt-16">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">How it works</h2>
          <div className="mt-5 space-y-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ delay: 0.05 * i }}
                className="ss-card p-5 flex gap-4" data-testid={`intro-step-${i}`}>
                <div className="flex-shrink-0 grid place-items-center h-11 w-11 rounded-full bg-[#F5F3ED]">
                  <s.icon size={20} strokeWidth={1.5} className="text-[#C9A227]" />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono-ss text-xs text-[#C9A227]">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="text-[16px] font-semibold">{s.title}</h3>
                  </div>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-[#474747]">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Social proof */}
        <section className="mt-14">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
            Made for people who keep functioning
          </h2>
          <div className="mt-5 space-y-3">
            {QUOTES.map((t, i) => (
              <motion.figure key={i}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ delay: 0.05 * i }}
                className="ss-card p-5" data-testid={`intro-quote-${i}`}>
                <blockquote className="font-serif-display text-[19px] leading-snug text-[#1A1A1A]">
                  “{t.q}”
                </blockquote>
                <figcaption className="mt-2 text-[12.5px] text-[#6B6B6B]">— {t.who}</figcaption>
              </motion.figure>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section className="mt-14">
          <div className="ss-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={18} strokeWidth={1.5} className="text-[#C9A227]" />
              <span className="text-[15px] font-medium">Private by design</span>
            </div>
            <p className="text-[13.5px] leading-relaxed text-[#474747]">
              You can start anonymously — nothing to sign up for. Your data is never sold to advertisers
              or shared with employers, and you can export or permanently delete everything at any time.
            </p>
          </div>
        </section>

        {/* Install + CTA */}
        <section className="mt-10 flex flex-col items-start gap-4">
          <button data-testid="intro-start-bottom"
            onClick={() => start(false)}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5">
            Enter Signal State ID <ArrowRight size={18} strokeWidth={1.8} />
          </button>
          {canInstall && (
            <button data-testid="intro-install" onClick={promptInstall}
              className="inline-flex items-center gap-2 text-[13px] text-[#8a6d10] hover:text-[#1A1A1A] ss-focus transition-colors">
              <Download size={15} strokeWidth={1.5} /> Add Signal State ID to your home screen — works offline
            </button>
          )}
          <button data-testid="intro-learn-states" onClick={() => nav("/operating-states")}
            className="text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors">
            Understanding operating states
          </button>
          <SafetyLink />
        </section>
      </div>
    </Shell>
  );
}
